import {
  markAppointmentFormsComplete,
  useAppointmentFormsMarkedComplete,
  type HealthieAnswer
} from "@internal/telemedicine"
import { useNow } from "@internal/ui"
import { useUploadIdDocuments } from "./useUploadIdDocuments"
import { usePhotoIdTracking } from "./usePhotoIdTracking"
import { useQueryClient } from "@tanstack/react-query"
import { TRPCClientError } from "@trpc/client"
import { useIntakeForms, removeCompletedIntakeForm } from "./useIntakeForms"
import { usePhotoIdOnFile } from "./usePhotoIdOnFile"
import { useSubmitIntakeForm } from "./useSubmitIntakeForm"

function isNotFoundError(error: unknown): boolean {
  if (!(error instanceof TRPCClientError)) return false
  const data = (error as TRPCClientError<never>).data as { code?: string } | undefined
  return data?.code === "NOT_FOUND"
}

export function usePreVisitIntake(appointmentId: string, startTime: string) {
  const queryClient = useQueryClient()
  useNow(new Date(startTime), "at-the-crossing-only")
  const appointmentHasStarted = !(Date.parse(startTime) > Date.now())
  const photoIdOnFile = usePhotoIdOnFile({ enabled: true, refreshOnMount: true })
  const upload = useUploadIdDocuments()
  const { onImageAdded } = usePhotoIdTracking()
  const intakeForms = useIntakeForms(
    appointmentId,
    !appointmentHasStarted && !photoIdOnFile.isFetching && !photoIdOnFile.isError && photoIdOnFile.data?.onFile === true
  )
  const submission = useSubmitIntakeForm(appointmentId)
  const formsMarkedComplete = useAppointmentFormsMarkedComplete(appointmentId)
  const forms = intakeForms.data?.forms
  const form = forms?.[0]
  // React Query v4 treats a no-data retry as loading rather than refetching. isFetched distinguishes
  // that retry from the initial request while keeping the query as the source of truth.
  const isPhotoIdRetrying = photoIdOnFile.isFetching && photoIdOnFile.isFetched && photoIdOnFile.data == null

  const submitAnswers = async (answers: HealthieAnswer[]) => {
    if (!form) throw new Error("No intake form is connected to this appointment.")
    await submission.mutateAsync({ formId: form.id, answers })
    if (forms.length === 1) markAppointmentFormsComplete(queryClient, appointmentId)
    removeCompletedIntakeForm(queryClient, appointmentId, form.id)
  }

  const retryPhotoIdStatus = (): void => {
    void photoIdOnFile.refetch()
  }

  return {
    appointmentHasStarted,
    questionnaire: {
      form,
      isLoading: intakeForms.isLoading,
      error: intakeForms.error,
      isNotFound: isNotFoundError(intakeForms.error),
      hasForms: intakeForms.data?.hasForms,
      isComplete: formsMarkedComplete || (forms != null && forms.length === 0),
      isReady: !intakeForms.isLoading && !intakeForms.error && forms != null && forms.length > 0,
      program: intakeForms.data?.program ?? null,
      submitAnswers,
      hasSubmissionError: submission.isError
    },
    photoId: {
      isLoading: (photoIdOnFile.isLoading || photoIdOnFile.isFetching) && !isPhotoIdRetrying,
      hasStatusError: photoIdOnFile.isError || isPhotoIdRetrying,
      isRetrying: isPhotoIdRetrying,
      isRequired: !photoIdOnFile.isError && photoIdOnFile.data?.onFile !== true && !upload.isSuccess,
      retryStatus: retryPhotoIdStatus,
      upload: upload.mutate,
      isUploading: upload.isPending,
      uploadError: upload.isError ? upload.error : null,
      trackImageAdded: onImageAdded
    }
  }
}
